import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { TextInputField } from "../../bootstrap/TextInputField";
import { SingleUploadArea } from "../../SingleUploadArea";
import { AuthenticatedDownloadLink } from "../../document/AuthenticatedDownloadLink";

function AsyncDocumentLinkCombo({
  documentType,
  documents,
  setDocuments,
  setError,
  uploadFunction,
  deleteFunction,
  entityRelated,
  readMode,
  hideDelete,
}) {
  const docs = Array.isArray(documents) ? documents : [];
  const d = docs.find((doc) => doc.documentType === documentType);

  if (readMode) {
    if (!d?.documentPath) return null;

    return (
      <>
        <label>
          <FormattedMessage id={`documentType.${d.documentType}`} />
        </label>
        <AuthenticatedDownloadLink url={d.documentPath} filename={d.name}>
          <small>{d.name}</small>
        </AuthenticatedDownloadLink>
      </>
    );
  }

  return (
    <SingleUploadArea
      documents={documents}
      setDocuments={setDocuments}
      documentType={documentType}
      setError={setError}
      entityRelated={entityRelated}
      processUploadCallback={uploadFunction}
      processDeleteCallback={hideDelete ? undefined : deleteFunction}
    />
  );
}

export function EaAccreditationFormStep7({
  formStep,
  numberOfSteps,
  errors,
  formikValues,
  handleChange,
  handlePreviousStep,
  handleNextStep,
  setError,
  documents,
  setDocuments,
  readMode: readModeProp = false,
  mode,
  uploadFunction,
  deleteFunction,
}) {
  const intl = useIntl();
  const baseReadMode = !!readModeProp;

  const entity = formikValues?.entity ?? {};
  const responsibles = Array.isArray(entity.responsibles)
    ? entity.responsibles
    : [];

  const r0 = responsibles[0] ?? { name: "", duty: "" };
  const r1 = responsibles[1] ?? { name: "", duty: "" };

  const isEaAccreditation = mode === "EA_ACCREDITATION";
  const isRenewalDraft = mode === "RENEWAL_DRAFT";
  const isRenewalLocked = mode === "RENEWAL_LOCKED";

  const readModeDocs = mode
    ? isEaAccreditation || isRenewalLocked
    : baseReadMode;

  const lockCheckboxes = mode
    ? isEaAccreditation || isRenewalLocked
    : baseReadMode;

  const lockResponsibles = mode
    ? isEaAccreditation || isRenewalDraft || isRenewalLocked
    : baseReadMode;

  const hideDelete = mode
    ? isEaAccreditation || isRenewalLocked
    : baseReadMode;

  return (
    <div>
      <h4 className="text-primary mb-0">
        <FormattedMessage id="eaAccreditationForm.step7.title" />
      </h4>

      <small className="text-secondary text-uppercase">
        {intl.formatMessage(
          { id: "all.step" },
          { step: formStep, numberOfSteps }
        )}
      </small>

      <Form.Label className="mb-3">
        <FormattedMessage id="eaAccreditationForm.veracityDeclaration.label" />
      </Form.Label>

      <div className="p-3 bg-light mb-3">
        {Array.from({ length: 9 }, (_, i) => (
          <p key={i}>
            <FormattedMessage
              id={`eaAccreditationForm.veracityDeclaration.text${i + 1}`}
            />
          </p>
        ))}
      </div>

      <Row className="mt-4">
        <small className="text-primary text-uppercase">
          <FormattedMessage id="eaAccreditationForm.step7.authorizationDeclarations.subtitle" />
        </small>
      </Row>

      <Row>
        <Col>
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step7.declarationsNote" />
          </Form.Label>
        </Col>
      </Row>

      <Row className="text-secondary font-weight-bold mt-2">
        <Col className="d-flex">
          <Form.Check
            type="checkbox"
            checked={!!entity.atDeclarationAuthorized}
            name="entity.atDeclarationAuthorized"
            onChange={handleChange}
            disabled={lockCheckboxes}
          />
          <FormattedMessage id="eaAccreditationForm.step7.at" />
        </Col>
      </Row>

      <Row className="text-secondary font-weight-bold mt-2">
        <Col className="d-flex">
          <Form.Check
            type="checkbox"
            checked={!!entity.ssDeclarationAuthorized}
            name="entity.ssDeclarationAuthorized"
            onChange={handleChange}
            disabled={lockCheckboxes}
          />
          <FormattedMessage id="eaAccreditationForm.step7.ss" />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          {!entity.atDeclarationAuthorized && (
            <AsyncDocumentLinkCombo
              documents={documents}
              setDocuments={setDocuments}
              documentType="AT_DECLARATION"
              setError={setError}
              entityRelated
              readMode={readModeDocs}
              hideDelete={hideDelete}
              uploadFunction={uploadFunction}
              deleteFunction={deleteFunction}
            />
          )}

          {!entity.ssDeclarationAuthorized && (
            <AsyncDocumentLinkCombo
              documents={documents}
              setDocuments={setDocuments}
              documentType="SS_DECLARATION"
              setError={setError}
              entityRelated
              readMode={readModeDocs}
              hideDelete={hideDelete}
              uploadFunction={uploadFunction}
              deleteFunction={deleteFunction}
            />
          )}
        </Col>
      </Row>

      <Row className="mt-5">
        <small className="text-primary text-uppercase">
          <FormattedMessage id="eaAccreditationForm.step7.responsibles.subtitle" />
        </small>
      </Row>

      {[r0, r1].map((r, idx) => {
        if (idx === 1 && (!r0.name?.trim() || !r0.duty?.trim())) return null;

        return (
          <React.Fragment key={idx}>
            <Row className="mt-5">
              <Col>
                <small className="text-uppercase">
                  <FormattedMessage id="eaAccreditationForm.step7.responsible" />{" "}
                  {idx + 1}
                </small>
              </Col>
            </Row>

            <Row>
              <Col>
                <TextInputField
                  labelId="eaAccreditationForm.step7.responsible.name"
                  name={`entity.responsibles[${idx}].name`}
                  handleChange={handleChange}
                  value={r.name || ""}
                  isInvalid={errors.entity?.responsibles?.[idx]?.name}
                  errorMessage={errors.entity?.responsibles?.[idx]?.name}
                  disabled={lockResponsibles}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextInputField
                  labelId="eaAccreditationForm.step7.responsible.duty"
                  name={`entity.responsibles[${idx}].duty`}
                  handleChange={handleChange}
                  value={r.duty || ""}
                  isInvalid={errors.entity?.responsibles?.[idx]?.duty}
                  errorMessage={errors.entity?.responsibles?.[idx]?.duty}
                  disabled={lockResponsibles}
                />
              </Col>
            </Row>
          </React.Fragment>
        );
      })}

      <div className="form-actions mt-5">
        {handlePreviousStep && (
          <button
            className="btn btn-link"
            type="button"
            onClick={handlePreviousStep}
          >
            <FormattedMessage id="all.back" />
          </button>
        )}

        {handleNextStep && (
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleNextStep}
          >
            <FormattedMessage id="all.nextStep" />
          </button>
        )}
      </div>
    </div>
  );
}
